import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Volume2, VolumeX } from 'lucide-react';
import { SITE_VIDEOS } from '../constants/publicAssets';
import { publicAbsoluteUrl } from '../lib/publicUrl';

/**
 * Full-viewport looping hero clip (`public/videos/video-hero.mp4`).
 * Autoplay stays **muted** (browser policy). Visitor uses **Sound on** for audio — no auto-unmute, so **Mute** always sticks.
 */
export function HeroCinematic() {
  const reduceMotion = useReducedMotion();
  const [videoFailed, setVideoFailed] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const videoRef = useRef(null);
  /** Keeps playback init (e.g. late `canplay`) from forcing mute after the user chose sound on. */
  const wantSoundRef = useRef(false);
  const videoSrc = publicAbsoluteUrl(SITE_VIDEOS.homeTeaser);

  useEffect(() => {
    if (videoFailed) return;
    const v = videoRef.current;
    if (!v) return;
    let cancelled = false;

    const startPlayback = () => {
      if (cancelled) return;
      v.muted = !wantSoundRef.current;
      v.volume = 1;
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          if (cancelled) return;
          void v.play().catch(() => {});
        });
      }
    };

    if (reduceMotion) {
      v.pause();
      v.currentTime = 0;
      return undefined;
    }

    if (v.readyState >= 2) startPlayback();
    else v.addEventListener('canplay', startPlayback, { once: true });

    return () => {
      cancelled = true;
      v.removeEventListener('canplay', startPlayback);
    };
  }, [reduceMotion, videoFailed, videoSrc]);

  const toggleHeroSound = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const next = !soundOn;
    wantSoundRef.current = next;
    setSoundOn(next);
    v.muted = !next;
    if (next) {
      v.volume = 1;
      void v.play().catch(() => {});
    }
  }, [soundOn]);

  return (
    <section
      id="home"
      className="relative min-h-[100svh] min-h-[100vh] flex flex-col justify-end overflow-hidden bg-black"
    >
      <div className="absolute inset-0 z-0">
        {!videoFailed ? (
          <div className="absolute inset-0 overflow-hidden">
            <video
              ref={videoRef}
              key={videoSrc}
              src={videoSrc}
              className="absolute left-1/2 top-1/2 min-h-full min-w-full -translate-x-1/2 -translate-y-1/2 object-cover pointer-events-none"
              style={{ width: 'max(120vw, 120vh * 1.78)' }}
              autoPlay={!reduceMotion}
              muted={!soundOn}
              loop={!reduceMotion}
              playsInline
              preload="auto"
              aria-hidden
              onError={() => setVideoFailed(true)}
            />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f1418] via-[#1a2228] to-[#0a0c0e]" aria-hidden />
        )}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/25"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40"
          aria-hidden
        />
        {!videoFailed && (
          <div className="absolute bottom-20 right-4 sm:right-8 md:bottom-24 z-[15] pointer-events-auto">
            <button
              type="button"
              onClick={toggleHeroSound}
              className="flex items-center gap-2 rounded-full border border-white/25 bg-black/45 px-3.5 py-2 text-[10px] font-mono uppercase tracking-[0.16em] text-white/90 backdrop-blur-md hover:bg-black/60 hover:border-white/35 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/70"
              aria-pressed={soundOn}
              aria-label={soundOn ? 'Mute hero video' : 'Unmute hero video — play audio'}
            >
              {soundOn ? (
                <Volume2 className="w-4 h-4 text-[#9fd4c9]" aria-hidden />
              ) : (
                <VolumeX className="w-4 h-4 text-zinc-400" aria-hidden />
              )}
              {soundOn ? 'Mute' : 'Sound on'}
            </button>
          </div>
        )}
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 sm:px-10 pb-16 sm:pb-20 md:pb-28 pt-32 md:pt-40 pointer-events-none">
        <motion.div
          initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-[42rem] pointer-events-auto"
        >
          <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.35em] text-white/55 mb-5">
            Athlete recovery
          </p>
          <h1 className="font-heading text-[clamp(2.5rem,6.5vw,4.25rem)] font-semibold leading-[0.92] tracking-[-0.04em] text-white">
            The best recovery shelf
            <span className="block font-editorial italic font-normal text-[#7ec8bc] mt-1">
              for runners &amp; athletes.
            </span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/70 font-light leading-relaxed max-w-md">
            Contrast therapy, targeted heat wraps, percussion, and TENS/EMS—pick the right tool for the situation, ship pan-India,
            recover like you train.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 sm:gap-4">
            <a
              href="#product-rail"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-neutral-950 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#c5ebe2] transition-colors"
            >
              Shop the shelf
              <ArrowUpRight className="w-4 h-4 opacity-80" aria-hidden />
            </a>
            <a
              href="#recovery-guide"
              className="inline-flex items-center justify-center px-8 py-3.5 border border-white/25 text-white text-xs font-semibold uppercase tracking-[0.2em] hover:bg-white/10 transition-colors"
            >
              Match product to need
            </a>
          </div>
        </motion.div>
      </div>

      <div className="relative z-10 flex justify-center pb-8 text-white/35 pointer-events-none">
        {reduceMotion ? (
          <div className="flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em]">
            <span>Scroll</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        ) : (
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
            className="flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em]"
          >
            <span>Scroll</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.div>
        )}
      </div>
    </section>
  );
}
